import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipGrid } from '@angular/material/chips';
import { without } from 'lodash';
import { Subscription } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { IconNames } from '../../common/constants/icon-names';
import { AutoUnsubscribe } from '../../common';
import { markRequiredControlWithAsteriskSymbol } from '../../common/utils/helpers';
import { AutocompleteErrorMatcher } from './autocomplete.error-matcher';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { AutocompleteValuePipe } from './autocomplete.pipe';

type SelectedItemsMapperInput<T> = {
  items: T[];
  selectedItems: T[];
  filterKey: string;
};

@Component({
  selector: 'ui-autocomplete',
  imports: [CommonModule, IntrackFeatureModule, TranslateModule, SvgIconComponent, AutocompleteValuePipe],
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class AutocompleteComponent<T> implements OnInit, ControlValueAccessor {
  @Input() set items(value: T[]) {
    this.autocompleteList = this.isMultiSelect
      ? this.selectedItemsMapper({
          items: value,
          selectedItems: this.chipsControl.value,
          filterKey: this.searchValueKey,
        })
      : value;
  }

  @Input() label: string;
  @Input() testId: string;
  @Input() searchValueKey = 'name';
  @Input() selectedValueMask = 'name';
  @Input() searchValueKeyWithAliases: [string, string][] = [];
  @Input() isMultiSelect = false;
  @Input() isChipRemovable = true;
  @Input() autocompleteItemTemplate: TemplateRef<T>;
  @Input() chipItemTemplate: TemplateRef<T>;
  @Input() tooltipText?: string;
  @Input() isLoadMoreEnabled = false;
  @Input() loadMoreAmount = 10;
  @Input() singleSelectionTooltipText?: string;

  //eslint-disable-next-line @angular-eslint/no-output-native
  @Output() search = new EventEmitter<string>();
  @Output() selectEvent = new EventEmitter<T>();
  @Output() removeEvent = new EventEmitter<T>();
  @Output() loadMore: EventEmitter<{ more: number; optionAmount: number; searchValue: string }> = new EventEmitter();

  @ViewChild('chipInput') chipInputRef: ElementRef;
  @ViewChild('autocomplete') autocomplete: MatAutocomplete;
  @ViewChild('chipList') chipGrid: MatChipGrid;

  isFieldRequired: (control: AbstractControl) => string = markRequiredControlWithAsteriskSymbol;

  searchControl = new UntypedFormControl('');
  chipsControl = new UntypedFormControl('');
  chipControl = new UntypedFormControl('');
  errorMatcher = new AutocompleteErrorMatcher(this.ngControl);

  disabled = false;
  autocompleteList: T[];
  iconNames = IconNames;

  private subscriptions: Subscription[] = [];

  constructor(@Self() public ngControl: NgControl, private cd: ChangeDetectorRef) {
    ngControl.valueAccessor = this;
  }

  registerOnChange(fn: () => T): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: T): void {
    if (this.isMultiSelect) {
      if (value) {
        this.chipsControl.setValue(value, { emitEvent: false });
      }
    } else {
      this.chipControl.setValue(value, {
        emitEvent: false,
      });
    }
  }

  onChange: (value: T | T[]) => T | T[];

  onTouched: () => void;

  setDisabledState(value: boolean): void {
    this.disabled = value;
    value ? this.chipControl.disable() : this.chipControl.enable();
    value ? this.chipsControl.disable() : this.chipsControl.enable();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          filter((searchStr: string) => typeof searchStr === 'string'),
          debounceTime(300),
          map((searchStr: string) => searchStr.trim().toLowerCase()),
        )
        .subscribe((searchStr: string) => {
          this.ngControl.control.updateValueAndValidity();
          this.search.emit(searchStr);
        }),
      this.ngControl.control.statusChanges.subscribe(() => {
        this.chipGrid?.updateErrorState();
        this.cd.markForCheck();
      }),
    );
  }

  onBlur(): void {
    if (this.chipInputRef.nativeElement.value) {
      this.chipInputRef.nativeElement.value = '';
    }
  }

  manualUpdateValidity(): void {
    this.errorMatcher = new AutocompleteErrorMatcher(this.ngControl);
    this.cd.detectChanges();
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value) {
      const value: T[] = event.option.value;
      this.onChange(value);
      this.chipControl.setValue(value, { emitEvent: false });
      this.selectEvent.emit(event.option.value);
    }

    if (this.chipInputRef.nativeElement) {
      this.chipInputRef.nativeElement.value = '';
    }
    this.searchControl.setValue('', { emitEvent: false });
  }

  onOptionSelectedForChipList(event: MatAutocompleteSelectedEvent): void {
    this.addChipAutocompleteOption(this.ngControl.control, this.chipInputRef.nativeElement, event.option.value);

    this.selectEvent.emit(event.option.value);
    this.autocompleteList = this.selectedItemsMapper({
      items: this.autocompleteList,
      selectedItems: this.chipsControl.value,
      filterKey: this.searchValueKey,
    });
  }

  removeChipItem(element: T): void {
    if (this.isMultiSelect) {
      this.ngControl.control.setValue(without(this.ngControl.control.value, element));
      this.autocompleteList = [...this.autocompleteList, element];
    } else {
      this.ngControl.control.reset();
    }
    this.ngControl.control.markAsDirty();
    this.removeEvent.emit(element);
  }

  onLoadMore(): void {
    const optionAmount = this.autocomplete.options.length;

    this.loadMore.emit({ more: this.loadMoreAmount, optionAmount, searchValue: this.searchControl.value });
  }

  private addChipAutocompleteOption(
    control: UntypedFormControl | AbstractControl,
    input: HTMLInputElement,
    autocompleteValue: T,
  ): void {
    if (autocompleteValue && this.isUniqueValue(control.value, autocompleteValue)) {
      const value: T[] = control.value ? [...control.value, autocompleteValue] : [autocompleteValue];
      this.onChange(value);
      this.chipsControl.setValue(value, { emitEvent: false });
    }

    if (input) {
      input.value = '';
    }
    this.searchControl.setValue('', { emitEvent: false });
  }

  private isUniqueValue(selectedValues: T[], newValue: T): boolean {
    return !selectedValues?.some((val: T) => JSON.stringify(val) === JSON.stringify(newValue));
  }

  private selectedItemsMapper({ items, selectedItems, filterKey }: SelectedItemsMapperInput<T>): T[] {
    if (this.searchValueKeyWithAliases?.length) {
      return this.multipleFilterKeyCheck({ items, selectedItems, filterKey });
    }
    return this.singleFilterKeyCheck({ items, selectedItems, filterKey });
  }

  private singleFilterKeyCheck({ items, selectedItems, filterKey }: SelectedItemsMapperInput<T>): T[] {
    return items.filter((item: T) => {
      return (selectedItems ?? []).every((selectedItem: T) => selectedItem[filterKey] !== item[filterKey]);
    });
  }

  private multipleFilterKeyCheck({ items, selectedItems, filterKey }: SelectedItemsMapperInput<T>): T[] {
    return items.filter((item: T) => {
      return this.searchValueKeyWithAliases.every((keyWithAlias: [string, string]) => {
        const aliasConditions = selectedItems.every((selectedItem: T) => {
          const firstAliasNotUndefined = !!selectedItem[keyWithAlias[0]] && !!item[keyWithAlias[0]];
          const secondAliasNotUndefined = !!selectedItem[keyWithAlias[1]] && !!item[keyWithAlias[1]];
          const filterKeyCondition = selectedItem[filterKey] !== item[filterKey];

          const firstAlias =
            (selectedItem[keyWithAlias[0]] !== item[keyWithAlias[0]] && firstAliasNotUndefined) ||
            selectedItem[keyWithAlias[0]] !== item[keyWithAlias[1]];
          const secondAlias =
            (selectedItem[keyWithAlias[1]] !== item[keyWithAlias[1]] && secondAliasNotUndefined) ||
            selectedItem[keyWithAlias[1]] !== item[keyWithAlias[0]];

          return firstAlias || secondAlias || filterKeyCondition;
        });

        return aliasConditions;
      });
    });
  }
}
